import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["switcherMenuHasBeenOpened"],
  _excluded2 = ["contextDependencies"],
  _excluded3 = ["switcherMenuHasBeenOpened"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { createActionBusWithContextsFactory } from './controllers/actions';
import { createContextStore } from './controllers/context';
import { createSurfaceStore } from './controllers/store';
import { buildCombinedKey } from './utils/buildCombinedKey';
import { getModulePathsInSurface } from './utils/getModulePathsInSurface';
import { setModuleStateAtPath } from './utils/setModuleState';
import { loadSurfaceExperience } from './utils/ufo';
var isEagerSurfaceInitialization = function isEagerSurfaceInitialization(surfaceInit) {
  return !surfaceInit.load || surfaceInit.load === 'EAGER';
};
var eagerLoadSurfaces = function eagerLoadSurfaces(surfaceLoader) {
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(surfaces, update, context) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            Object.entries(surfaces).forEach( /*#__PURE__*/function () {
              var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
                var _ref4, name, surfaceInit, surfaceType, surfaceKey, fallback, experienceMetaData, surfaceLoaderPromise, _yield$surfaceLoader, surface, error;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) switch (_context.prev = _context.next) {
                    case 0:
                      _ref4 = _slicedToArray(_ref2, 2), name = _ref4[0], surfaceInit = _ref4[1];
                      if (isEagerSurfaceInitialization(surfaceInit)) {
                        _context.next = 3;
                        break;
                      }
                      return _context.abrupt("return");
                    case 3:
                      surfaceType = surfaceInit.surfaceType, surfaceKey = surfaceInit.surfaceKey, fallback = surfaceInit.fallback;
                      experienceMetaData = {
                        metadata: {
                          surfaceType: surfaceType,
                          surfaceKey: surfaceKey
                        }
                      };
                      surfaceLoaderPromise = loadSurfaceExperience.start();
                      _context.next = 8;
                      return surfaceLoader({
                        surfaceType: surfaceType,
                        surfaceKey: surfaceKey,
                        contextResolver: function contextResolver() {
                          var _ref5 = context || {
                              switcherMenuHasBeenOpened: true
                            },
                            switcherMenuHasBeenOpened = _ref5.switcherMenuHasBeenOpened,
                            rest = _objectWithoutProperties(_ref5, _excluded);
                          return rest;
                        }
                      });
                    case 8:
                      _yield$surfaceLoader = _context.sent;
                      surface = _yield$surfaceLoader.surface;
                      error = _yield$surfaceLoader.error;
                      _context.next = 13;
                      return surfaceLoaderPromise;
                    case 13:
                      if (!surface) {
                        _context.next = 16;
                        break;
                      }
                      _context.next = 16;
                      return loadSurfaceExperience.success(experienceMetaData);
                    case 16:
                      update(name, {
                        surface: surface,
                        isLoading: !surface,
                        fallback: fallback,
                        error: error
                      });
                    case 17:
                    case "end":
                      return _context.stop();
                  }
                }, _callee);
              }));
              return function (_x4) {
                return _ref3.apply(this, arguments);
              };
            }());
          case 1:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x, _x2, _x3) {
      return _ref.apply(this, arguments);
    };
  }();
};
var isSurfaceStore = function isSurfaceStore(initializationOptions) {
  return !!initializationOptions.getCurrentSurfaceState;
};
var createMutateAtModuleFactory = function createMutateAtModuleFactory(getCurrentState, update) {
  return function (moduleType, moduleKey) {
    return function (mutation) {
      var state = getCurrentState();
      update(state.name, function (_ref6) {
        var _get;
        var surface = _ref6.surface;
        var state = getCurrentState();
        var modulePaths = state.surface ? getModulePathsInSurface(state.surface) : new Map();
        if (!surface) {
          return {
            surface: surface
          };
        }
        var modulePath = modulePaths.get(buildCombinedKey(moduleType, moduleKey));
        if (!modulePath) {
          throw Error("tried to update surface module with id of ".concat(moduleKey, " path but could not find that module"));
        }
        var currentModuleConfiguration = (_get = get(state.surface, modulePath)) === null || _get === void 0 ? void 0 : _get.fields;
        if (typeof mutation === 'function' && !currentModuleConfiguration) {
          throw Error("tried to access currentState of surface module with id of ".concat(moduleKey, " path but could not find that module"));
        }
        return {
          surface: setModuleStateAtPath(modulePath)(surface, typeof mutation === 'function' ? mutation(currentModuleConfiguration) : mutation)
        };
      });
    };
  };
};
var isContextReady = function isContextReady() {
  var contextDependencies = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return function (subscriberContext) {
    return contextDependencies.every(function (dep) {
      if (typeof dep === 'string') {
        return Object.keys(subscriberContext).includes(dep);
      }
      if (dep !== null && dep !== void 0 && dep.match) {
        var _subscriberContext$de;
        return !!(subscriberContext !== null && subscriberContext !== void 0 && (_subscriberContext$de = subscriberContext[dep.field]) !== null && _subscriberContext$de !== void 0 && _subscriberContext$de.match(dep.match));
      }
      if (dep !== null && dep !== void 0 && dep.required) {
        var _subscriberContext$de2;
        return !!((_subscriberContext$de2 = subscriberContext === null || subscriberContext === void 0 ? void 0 : subscriberContext[dep.field]) !== null && _subscriberContext$de2 !== void 0 ? _subscriberContext$de2 : false);
      }
      return true;
    });
  };
};
var transformSurfacesToSubscribers = function transformSurfacesToSubscribers(surfaceLoader, update) {
  return function (surfaces) {
    return Object.entries(surfaces).filter(function (surface) {
      return surface[1].load === 'LAZY' || surface[1].load === undefined;
    }).map(function (_ref8) {
      var _ref9 = _slicedToArray(_ref8, 2),
        surfaceName = _ref9[0],
        _ref7 = _ref9[1];
      var contextDependencies = _ref7.contextDependencies,
        surface = _objectWithoutProperties(_ref7, _excluded2);
      return [function (_ref10) {
        var subscriberContext = _ref10.subscriberContext;
        if (!isContextReady(contextDependencies)(subscriberContext)) {
          return;
        }
        eagerLoadSurfaces(surfaceLoader)(_defineProperty({}, surfaceName, surface), update, subscriberContext);
      }, contextDependencies === null || contextDependencies === void 0 ? void 0 : contextDependencies.map(function (dep) {
        return typeof dep === 'string' ? dep : dep.field;
      }), {
        updateOnSubscribe: true
      }];
    });
  };
};
export var createSurfaceSdk = function createSurfaceSdk(_ref11) {
  var surfaces = _ref11.surfaces,
    surfaceLoader = _ref11.surfaceLoader,
    _ref11$sideEffects = _ref11.sideEffects,
    sideEffects = _ref11$sideEffects === void 0 ? [] : _ref11$sideEffects,
    _ref11$context = _ref11.context,
    context = _ref11$context === void 0 ? {} : _ref11$context,
    contextMiddleware = _ref11.contextMiddleware;
  var contextStore = createContextStore({
    initialState: context,
    middleware: contextMiddleware
  });
  var store = isSurfaceStore(surfaces) ? surfaces : function () {
    var _store = createSurfaceStore(surfaces);
    var surfaceToSubs = transformSurfacesToSubscribers(surfaceLoader, _store.update);
    contextStore.subscribe.apply(contextStore, _toConsumableArray(surfaceToSubs(surfaces)));
    return _store;
  }();
  var useSurface = function useSurface(surfaceName, analyticsSideEffect) {
    var _state$surface$type, _state$surface;
    var _useState = useState(store.getCurrentSurfaceState(surfaceName)),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    var createMutateAtModule = useCallback(createMutateAtModuleFactory(function () {
      return store.getCurrentSurfaceState(surfaceName);
    }, store.update), []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    var createActionBusWithContexts = useMemo(function () {
      return createActionBusWithContextsFactory({
        sideEffects: analyticsSideEffect ? [].concat(_toConsumableArray(sideEffects), [analyticsSideEffect]) : sideEffects
      });
    }, [analyticsSideEffect]);
    var lazyLoadSurface = useCallback( /*#__PURE__*/function () {
      var _ref12 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(surfaceType, surfaceKey) {
        var experienceMetaData, surfaceLoaderPromise, _yield$surfaceLoader2, surface, error;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.prev = 0;
              store.getCurrentSurfaceStateFromKeys(surfaceType, surfaceKey);
              return _context3.abrupt("return");
            case 5:
              _context3.prev = 5;
              _context3.t0 = _context3["catch"](0);
              experienceMetaData = {
                metadata: {
                  surfaceType: surfaceType,
                  surfaceKey: surfaceKey
                }
              };
              surfaceLoaderPromise = loadSurfaceExperience.start();
              _context3.next = 11;
              return surfaceLoader({
                surfaceType: surfaceType,
                surfaceKey: surfaceKey,
                contextResolver: function contextResolver() {
                  var context = contextStore.getCurrentContext();
                  var _ref13 = context || {
                      switcherMenuHasBeenOpened: true
                    },
                    switcherMenuHasBeenOpened = _ref13.switcherMenuHasBeenOpened,
                    rest = _objectWithoutProperties(_ref13, _excluded3);
                  return rest;
                }
              });
            case 11:
              _yield$surfaceLoader2 = _context3.sent;
              surface = _yield$surfaceLoader2.surface;
              error = _yield$surfaceLoader2.error;
              _context3.next = 16;
              return surfaceLoaderPromise;
            case 16:
              if (!surface) {
                _context3.next = 21;
                break;
              }
              _context3.next = 19;
              return loadSurfaceExperience.success(experienceMetaData);
            case 19:
              _context3.next = 23;
              break;
            case 21:
              _context3.next = 23;
              return loadSurfaceExperience.failure(experienceMetaData);
            case 23:
              store.update(surfaceName, {
                error: error,
                surface: surface,
                isLoading: false
              });
            case 24:
            case "end":
              return _context3.stop();
          }
        }, _callee3, null, [[0, 5]]);
      }));
      return function (_x5, _x6) {
        return _ref12.apply(this, arguments);
      };
    }(), [surfaceName]);
    useEffect(function () {
      var unsubscribe = store.subscribe(setState, [surfaceName], {
        updateOnSubscribe: true
      });
      var surfaceInit = store.getSurfaceInitializationOptions(surfaceName);
      var currentState = store.getCurrentSurfaceState(surfaceName);
      if ((surfaceInit === null || surfaceInit === void 0 ? void 0 : surfaceInit.load) === 'LAZY' && surfaceInit !== null && surfaceInit !== void 0 && surfaceInit.surfaceType && surfaceInit !== null && surfaceInit !== void 0 && surfaceInit.surfaceKey || surfaceInit && isEagerSurfaceInitialization(surfaceInit) && (currentState === null || currentState === void 0 ? void 0 : currentState.error) instanceof Error // Try to reload if eager load has previously failed
      ) {
        lazyLoadSurface(surfaceInit.surfaceType, surfaceInit.surfaceKey);
      }
      return unsubscribe;
    }, [surfaceName, lazyLoadSurface]);
    return _objectSpread({
      createMutateAtModule: createMutateAtModule,
      createActionBusAtModule: createActionBusWithContexts({
        client: contextStore.getCurrentContext(),
        surfaceName: (_state$surface$type = (_state$surface = state.surface) === null || _state$surface === void 0 ? void 0 : _state$surface.type) !== null && _state$surface$type !== void 0 ? _state$surface$type : surfaceName // Use surface type from server, otherwise use the surface name given.
      }),

      context: contextStore.getCurrentContext()
    }, state);
  };
  return {
    useSurface: useSurface,
    updateContext: contextStore.update,
    store: store
  };
};

// Public exports

export { createSurfaceRenderer } from './controllers/surfaceRenderer';
export { createSurfaceLoader } from './services/surface-loader';